import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { css } from 'config/emotionCss';
import {
  markAsRead,
  markAsUnread,
  deleteNotification,
  disabledNotificationSettingQuickly,
} from 'modules/notifications/actions';
import { NOTIFICATION_STATUS } from 'modules/notifications/constants';
import { isNotificationLoadingSelector } from 'modules/notifications/selectors/ui/actions';
import { getCurrentUserId, isImpersonatingUser, userNotificationConfigIsEnabled } from 'modules/users/selectors/currentUser';
import { Button, Dropdown } from 'ui/base';
import { useModal } from 'ui/base/Modal';
import { humanize, lowercaseFirstLetter } from 'utils/global';

import { Icon } from './menuItem/Shared';

const dropdownStyle = css`
  max-width: 350px;

  > li {
    &.ant-dropdown-menu-item-danger {
      &:hover {
        > i.ant-dropdown-menu-item-icon {
          color: inherit !important;
        }
      }
    }
  }
`;

/**
 * @param {{ isImpersonating: boolean, isUnread: boolean, enabled: boolean, setting: import('modules/notifications/types').NotificationSetting, extension: string }} params
 * @returns {import('ui/base/Dropdown').FBItemType[]}
 */
const getMenuItems = ({ isImpersonating, isUnread, enabled, extension, setting }) => [
  {
    key: 'markAsRead',
    'data-cy': 'notification-read',
    hide: isImpersonating || !isUnread,
    icon: <Icon className="fb-icon--check" />,
    label: <FormattedMessage id="notifications.menu.markAsRead" defaultMessage="Mark as read" />,
  },
  {
    key: 'markAsUnread',
    'data-cy': 'notification-unread',
    hide: isImpersonating || isUnread,
    icon: <Icon className="fb-icon--check" />,
    label: <FormattedMessage id="notifications.menu.markAsUnread" defaultMessage="Mark as unread" />,
  },
  {
    key: 'disabledNotificationType',
    'data-cy': 'notification-event-management',
    hide: !enabled,
    icon: <Icon className="fb-icon--close" />,
    label: (
      <FormattedMessage
        id="notifications.menu.disabled"
        defaultMessage="Stop receiving notifications when {notificationSetting}"
        values={{
          notificationSetting: (
            <FormattedMessage
              id={`notifications.navigation.table.setting.${extension}.${setting}`}
              defaultMessage={humanize(setting.toLowerCase())}
            >
              {(chunks) => chunks.map((chunk) => <span>{lowercaseFirstLetter(chunk)}</span>)}
            </FormattedMessage>
          ),
        }}
      />
    ),
  },
  {
    type: 'divider',
    hide: isImpersonating,
  },
  {
    danger: true,
    key: 'removeEntity',
    hide: isImpersonating,
    'data-cy': 'notification-delete',
    icon: <Icon className="fb-icon--delete text-danger" />,
    label: <FormattedMessage id="notifications.menu.delete.menuTitle" defaultMessage="Delete this notification" />,
  },
];

/** @type {React.FC<import('modules/notifications/types').Notification>} */
const NotificationDropdown = ({ id: notificationId, setting, extension, status }) => {
  const modal = useModal();
  const intl = useIntl();
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const isImpersonating = useSelector(isImpersonatingUser);
  const loading = useSelector((state) => isNotificationLoadingSelector(state, notificationId));
  const enabled = useSelector((state) => userNotificationConfigIsEnabled(state, { setting, extension }));
  const hasOptions = !isImpersonating || enabled;

  const isUnread = status === NOTIFICATION_STATUS.UNREAD;

  const handleDeleteNotification = React.useCallback(() => {
    dispatch(deleteNotification({ notificationId, userId }));
  }, [dispatch, notificationId, userId]);

  const handleConfirmRemove = React.useCallback(() => {
    modal.delete({
      content: intl.formatMessage({
        id: 'notifications.menu.delete.message',
        defaultMessage: 'Do you want to delete this notification?',
      }),
      onOk: async () => handleDeleteNotification(),
      cancelButtonProps: { 'data-cy': 'notification-delete-cancel' },
      okButtonProps: { 'data-cy': 'notification-delete-confirm' },
    });
  }, [modal, intl, handleDeleteNotification]);

  const actionCollection = {
    markAsRead: () => dispatch(markAsRead({ notificationId, userId })),
    markAsUnread: () => dispatch(markAsUnread({ notificationId, userId })),
    disabledNotificationType: () => dispatch(disabledNotificationSettingQuickly(extension, setting, notificationId)),
    removeEntity: handleConfirmRemove,
  };

  const menu = {
    className: dropdownStyle,
    onClick: ({ key: action }) => actionCollection[action]?.(),
    items: getMenuItems({ isImpersonating, isUnread, enabled, extension, setting }),
  };

  return (
    hasOptions && (
      <Dropdown menu={menu}>
        <Button
          shape="circle"
          loading={loading}
          icon={<span className="fb-icon--elipsis-v text-xl" />}
          data-cy="notification-options-open"
          className="border-0 bg-transparent"
        />
      </Dropdown>
    )
  );
};

export default NotificationDropdown;
